import type { FeedbackStatus, PortalNoteView } from "@anota/shared";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { MineApi } from "../backend";
import type { Strings } from "../i18n";
import { Conversation, findElement, useReposition } from "./Pins";
import { ago } from "./time";

/**
 * «Tus notas»: las que dejó quien está en la página (desde este navegador o con su enlace de revisión), con
 * su estado, lo que se hizo y la conversación con el equipo. Una lista (lo último que se movió, arriba; lo
 * nuevo, marcado) y, al abrir una, en qué punto está, dónde está en la página y la conversación.
 */

type Note = PortalNoteView;
type Anchor = NonNullable<Note["anchor"]>;
type Filter = "all" | "active" | "closed";

const ACTIVE: FeedbackStatus[] = ["open", "in_progress"];
/** Con pocas notas, los filtros sobran. */
const FILTER_FROM = 4;

/** La página de la nota: su título o, si no lo hay, la ruta. */
function pageName(n: Note): string {
  if (n.pageTitle) return n.pageTitle;
  try {
    return new URL(n.pageUrl).pathname;
  } catch {
    return n.pageUrl;
  }
}

const samePage = (n: Note) => {
  try {
    const u = new URL(n.pageUrl);
    return u.origin === location.origin && u.pathname === location.pathname;
  } catch {
    return false;
  }
};

/** Lo último que pasó en la nota: se envió, se cerró o alguien escribió. */
const lastActivity = (n: Note) => Math.max(Date.parse(n.createdAt), n.resolvedAt ? Date.parse(n.resolvedAt) : 0, ...n.comments.map((c) => Date.parse(c.createdAt)));

function ChatIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-5 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
    </svg>
  );
}

/** Lo señalado (el elemento, su punto o el área dibujada), marcado sobre la página: sigue al scroll. */
function Where({ el, a }: { el: Element; a: Anchor }) {
  useReposition();
  const r = el.getBoundingClientRect();
  if (a.kind === "point" && a.point)
    return (
      <div
        class="pin"
        style={{
          left: `${r.left + a.point.x * r.width}px`,
          top: `${r.top + a.point.y * r.height}px`,
        }}
      />
    );
  const f = a.kind === "area" && a.area ? a.area : { x: 0, y: 0, width: 1, height: 1 };
  return (
    <div
      class="hl fixed"
      style={{
        left: `${r.left + f.x * r.width}px`,
        top: `${r.top + f.y * r.height}px`,
        width: `${f.width * r.width}px`,
        height: `${f.height * r.height}px`,
      }}
    />
  );
}

/** En qué punto está: enviada → en curso → resuelta (o enviada → cerrada, si no se hará o está repetida). */
function Steps({ note, t }: { note: Note; t: Strings }) {
  const closed = note.status === "wont_fix" || note.status === "duplicate";
  const labels = closed ? [t.stepSent, t.status[note.status]] : [t.stepSent, t.status.in_progress, t.status.resolved];
  const at = closed ? 1 : ["open", "in_progress", "resolved"].indexOf(note.status);
  return (
    <div class={`s-${note.status}`}>
      <ol class="steps">
        {labels.map((label, i) => (
          <li key={label} class={i <= at ? "on" : undefined} aria-current={i === at ? "step" : undefined}>
            {label}
          </li>
        ))}
      </ol>
      <p class="md-hint">{t.statusHint[note.status]}</p>
    </div>
  );
}

/** El enlace a su portal, para ver sus notas en otro dispositivo (el móvil, otro navegador). */
function OtherDevice({ t, link }: { t: Strings; link: () => Promise<string> }) {
  const [state, setState] = useState<"" | "busy" | "copied" | "manual" | "error">("");
  const [url, setUrl] = useState<string | null>(null);
  async function copy() {
    setState("busy");
    try {
      const u = url ?? (await link());
      setUrl(u);
      try {
        await navigator.clipboard.writeText(u);
        setState("copied");
      } catch {
        // Sin portapapeles (o el navegador no deja copiar tras esperar al servidor): se enseña para copiarlo a mano.
        setState("manual");
      }
    } catch {
      setState("error");
    }
  }
  return (
    <div class="mine-device">
      <span>{t.otherDevice}</span>
      <button type="button" class="link" disabled={state === "busy"} onClick={() => void copy()}>
        {t.copyLink}
      </button>
      {state === "copied" && <p role="status">{t.linkCopied}</p>}
      {state === "manual" && url && (
        <label>
          {t.linkManual}
          <input readOnly value={url} onFocus={(e) => (e.currentTarget as HTMLInputElement).select()} />
        </label>
      )}
      {state === "error" && (
        <p class="err" role="alert">
          {t.error}
        </p>
      )}
    </div>
  );
}

function Item({ n, t, isNew, onOpen }: { n: Note; t: Strings; isNew: boolean; onOpen: () => void }) {
  return (
    <li>
      <button type="button" class={isNew ? "new" : undefined} onClick={onOpen}>
        <span class="mi-top">
          <span class={`np-status s-${n.status}`}>{t.status[n.status]}</span>
          <span class="mi-num">#{n.number}</span>
          {isNew && <span class="nl-new">{t.isNew}</span>}
          <time class="mi-when" dateTime={new Date(lastActivity(n)).toISOString()}>
            {ago(new Date(lastActivity(n)).toISOString(), t.lang)}
          </time>
        </span>
        <span class="mi-desc">{n.description}</span>
        <span class="mi-meta">
          <span>{samePage(n) ? t.thisPage : pageName(n)}</span>
          {n.comments.length > 0 && (
            <span class="mi-replies">
              <ChatIcon /> {t.replies(n.comments.length)}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

export function MyNotes({
  api,
  t,
  project,
  onClose,
  onNews,
}: {
  api: MineApi;
  t: Strings;
  /** El nombre del proyecto, para la cabecera. */
  project: string;
  onClose: () => void;
  onNews: (n: number) => void;
}) {
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [where, setWhere] = useState<Element | null>(null);
  const close = useRef<HTMLButtonElement>(null);

  const load = useCallback(() => {
    setFailed(false);
    api.list().then(
      (items) => setNotes([...items].sort((a, b) => lastActivity(b) - lastActivity(a))),
      () => setFailed(true),
    );
  }, [api]);
  useEffect(() => load(), [load]);
  useLayoutEffect(() => {
    close.current?.focus();
    setWhere(null);
  }, [open]);

  const current = notes?.find((n) => n.id === open);
  // Abrirla la deja vista (también cuando llega una respuesta con el panel abierto); y se recuentan las novedades.
  useEffect(() => {
    if (current) api.seen(current);
    if (notes) onNews(notes.filter(api.isNew).length);
  }, [notes, current]);

  // Lo marcado en la página se quita solo al rato.
  useEffect(() => {
    if (!where) return;
    const id = setTimeout(() => setWhere(null), 6000);
    return () => clearTimeout(id);
  }, [where]);

  // Al cerrar, todo queda visto: en la lista ya vio el estado de cada una (si no, el aviso no se iría nunca).
  const done = () => {
    if (notes) {
      api.seenAll(notes);
      onNews(0);
    }
    onClose();
  };

  // Esc: de una nota vuelve a la lista; de la lista, cierra.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (open) setOpen(null);
      else done();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [open, onClose, notes]);

  const target = current?.anchor && samePage(current) ? findElement(current.anchor) : null;
  const active = notes?.filter((n) => ACTIVE.includes(n.status)) ?? [];
  const shown = !notes ? [] : filter === "active" ? active : filter === "closed" ? notes.filter((n) => !ACTIVE.includes(n.status)) : notes;
  const filters: [Filter, string, number][] = notes
    ? [
        ["all", t.filterAll, notes.length],
        ["active", t.filterActive, active.length],
        ["closed", t.filterClosed, notes.length - active.length],
      ]
    : [];

  return (
    <>
      {where && current?.anchor && <Where el={where} a={current.anchor} />}
      <div class="panel mine" role="dialog" aria-label={current ? t.noteN(current.number) : t.mine}>
        <div class="mine-head">
          {current ? (
            <button type="button" class="back" onClick={() => setOpen(null)}>
              <span aria-hidden="true">←</span> {t.mine}
            </button>
          ) : (
            <div class="mine-title">
              <h2>{t.mine}</h2>
              <span>
                {project}
                {notes?.length ? ` · ${t.notesCount(notes.length)}` : ""}
              </span>
            </div>
          )}
          <button ref={close} type="button" class="x" aria-label={t.close} title={t.close} onClick={done}>
            ✕
          </button>
        </div>

        <div class="mine-body">
          {current ? (
            <div class="mine-note">
              <div class="md-title">
                <h3>{t.noteN(current.number)}</h3>
                <span class="md-type">{t.types[current.type]}</span>
                <time dateTime={current.createdAt}>{ago(current.createdAt, t.lang)}</time>
              </div>
              {/* Lo que pidió y dónde; luego en qué punto va, qué se hizo y la conversación. */}
              <Conversation key={current.id} note={current} t={t} reply={api.reply} verdict={api.verdict} onReplied={load} mine>
                <p class="md-page">
                  {t.onPage}{" "}
                  {samePage(current) ? (
                    t.thisPage
                  ) : (
                    <a href={current.pageUrl} target="_top">
                      {pageName(current)}
                    </a>
                  )}
                  {target && (
                    <button
                      type="button"
                      class="where"
                      onClick={() => {
                        // Arriba de la ventana: en el móvil, el panel ocupa la parte de abajo.
                        scrollBy({
                          top: target.getBoundingClientRect().top - innerHeight * 0.2,
                          behavior: "smooth",
                        });
                        setWhere(target);
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2" />
                        <circle cx="12" cy="12" r="3" fill="currentColor" />
                      </svg>
                      {t.showWhere}
                    </button>
                  )}
                </p>
                <Steps note={current} t={t} />
              </Conversation>
            </div>
          ) : failed ? (
            <div class="mine-state">
              <p class="err" role="alert">
                {t.mineError}
              </p>
              <button type="button" class="btn" onClick={load}>
                {t.retry}
              </button>
            </div>
          ) : !notes ? (
            <div class="mine-skel" aria-busy="true">
              <span class="sr">{t.loading}</span>
              <i />
              <i />
              <i />
            </div>
          ) : !notes.length ? (
            <div class="mine-state">
              <span class="mine-icon">
                <ChatIcon size={22} />
              </span>
              <b>{t.mineEmptyTitle}</b>
              <p>{t.mineEmpty}</p>
            </div>
          ) : (
            <>
              {notes.length >= FILTER_FROM && (
                <div class="mine-filter" role="group" aria-label={t.mine}>
                  {filters.map(([key, label, count]) => (
                    <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}>
                      {label} <span>{count}</span>
                    </button>
                  ))}
                </div>
              )}
              <ul class="mine-list">
                {shown.map((n) => (
                  <Item key={n.id} n={n} t={t} isNew={api.isNew(n)} onOpen={() => setOpen(n.id)} />
                ))}
              </ul>
              <OtherDevice t={t} link={api.link} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
